/**
 * Creates a deep clone of an object or primitive.
 *
 * @param {any} obj - The object or primitive to be cloned.
 * @param {WeakMap} [cloneMap=new WeakMap()] - A map to track already cloned objects to handle circular references.
 * @returns {any} The deep cloned object or the original value if it's a primitive.
 *
 * @example
 * // Cloning a simple object
 * const original = { a: 1, b: { c: 2 } };
 * const cloned = deepClone(original);
 * console.log(cloned); // { a: 1, b: { c: 2 } }
 *
 * @example
 * // Cloning an object with circular reference
 * const circularObj = {};
 * circularObj.self = circularObj;
 * const clonedCircular = deepClone(circularObj);
 * console.log(clonedCircular.self === clonedCircular); // true
 */
export function deepClone(obj, cloneMap = new WeakMap()) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    if (cloneMap.has(obj)) {
        return cloneMap.get(obj);
    }
    let clone;
    if (obj instanceof Date) {
        clone = new Date(obj.getTime());
    }
    else if (obj instanceof Map) {
        clone = new Map();
        cloneMap.set(obj, clone);
        obj.forEach((value, key) => {
            clone.set(key, deepClone(value, cloneMap));
        });
    }
    else if (obj instanceof Set) {
        clone = new Set();
        cloneMap.set(obj, clone);
        obj.forEach(value => {
            clone.add(deepClone(value, cloneMap));
        });
    }
    else if (Array.isArray(obj)) {
        clone = [];
        cloneMap.set(obj, clone);
        obj.forEach((item, index) => {
            clone[index] = deepClone(item, cloneMap);
        });
    }
    else {
        clone = obj.constructor ? new obj.constructor() : Object.create(null);
        cloneMap.set(obj, clone);
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                clone[key] = deepClone(obj[key], cloneMap);
            }
        }
    }
    return clone;
}
